import { useCallback, useEffect, useMemo, useRef } from "react";
import classNames from "classnames";
import { useFormik } from "formik";
import * as Yup from "yup";

import AscTextInput from "../../AscInputFields/AscTextInput/AscTextInput";
import AscHeading from "../../AscTypography/AscHeading/AscHeading";
import AscFilesUploader from "../../AscFilesUploader/AscFilesUploader";
import AscAddModuleFooter from "../AscAddModuleFooter/AscAddModuleFooter";

import styles from "./AscAddModuleInfo.module.scss";
import { AdminModuleInfo } from "@ascendnow/shared";
import AscRichTextEditor from "../../AscRichTextEditor/AscRichTextEditor";
import { LoadInitialValuePluginRef } from "../../AscRichTextEditor/plugins/LoadInitialValuePlugin/LoadInitialValuePlugin";

export interface AscAddModuleInfoProps {
  className?: string;
  header: string;
  value: number;
  currentModuleInfo?: AdminModuleInfo;
  setValue: (value: number) => void;
  handleModalData: (moduleObj: AdminModuleInfo) => void;
}

export default function AscAddModuleInfo({
  className,
  header,
  value,
  currentModuleInfo,
  setValue,
  handleModalData,
}: AscAddModuleInfoProps) {
  const descriptionRichTextRef = useRef<LoadInitialValuePluginRef>(null);
  const formik = useFormik<AdminModuleInfo>({
    initialValues: {
      name: (currentModuleInfo?.name as string) || "",
      description: currentModuleInfo?.description || "",
      image: currentModuleInfo?.image,
      coinsNum: currentModuleInfo?.coinsNum || 0,
      pointsNum: currentModuleInfo?.pointsNum || 0,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      coinsNum: Yup.number().required("Number of coins is required"),
      pointsNum: Yup.number().required("Number of points is required"),
      image: Yup.mixed().required("Image is required"),
    }),
    onSubmit: (values) => {
      handleModalData(values);
    },
  });
  const touched = useMemo(() => {
    return (
      Object.keys(formik.touched)?.filter(
        (key) => formik.touched?.[key as keyof typeof formik.touched]
      ).length > 0
    );
  }, [formik.touched]);

  const handleUpload = useCallback((acceptedFiles: File[]) => {
    formik.setFieldValue("image", acceptedFiles[0]);
  }, []);

  const handleRemove = useCallback(() => {
    formik.setFieldValue("image", null);
  }, []);

  const submitAndNext = () => {
    formik?.handleSubmit();
    setValue(value + 1);
  };

  useEffect(() => {
    if (!currentModuleInfo) return;
    descriptionRichTextRef.current?.reset({
      inferString: currentModuleInfo?.description || "",
    });
    formik.setValues({
      name: (currentModuleInfo?.name as string) || "",
      description: currentModuleInfo?.description || "",
      image: currentModuleInfo?.image,
      coinsNum: currentModuleInfo?.coinsNum || 0,
      pointsNum: currentModuleInfo?.pointsNum || 0,
    });
  }, [currentModuleInfo]);

  return (
    <div className={classNames(styles.ground, className)}>
      <AscHeading className={styles.header} level={6}>
        {header}
      </AscHeading>

      <div className={styles.form}>
        <div className={styles.inputField}>
          <label className={styles.fieldLabel} htmlFor="name">
            Module name
          </label>
          <AscTextInput
            placeholder="Module 1: Persistence"
            id="name"
            name="name"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.name}
          />
        </div>

        <div className={styles.inputField}>
          <label className={styles.fieldLabel} htmlFor="coinsNum">
            Number of coins
          </label>
          <AscTextInput
            className={classNames(styles.input)}
            fullWidth={true}
            id="coinsNum"
            name="coinsNum"
            type="number"
            step={1}
            min={0}
            placeholder="Number of coins"
            onChange={formik.handleChange}
            value={formik.values.coinsNum}
          />
        </div>

        <div className={classNames(styles.inputField, styles.fileInputField)}>
          <label className={styles.title}>Thumbnail</label>
          <AscFilesUploader
            name="image"
            type="image"
            accept={{
              "image/*": [".jpeg", ".png"],
            }}
            multiple={false}
            onDrop={handleUpload}
            onRemoveFile={handleRemove}
            srcFile={(() => {
              try {
                if (formik?.values?.image instanceof File) {
                  return URL.createObjectURL(formik.values.image);
                } else if (formik?.values?.image?.location) {
                  return formik?.values?.image?.location;
                }
              } catch (e) {
                console.error(e);
              }
              return undefined;
            })()}
            initialValue={
              formik.values.image instanceof File
                ? [formik.values.image]
                : undefined
            }
          />
        </div>

        <div className={styles.inputField}>
          <label className={styles.fieldLabel} htmlFor="pointsNum">
            Number of points
          </label>
          <AscTextInput
            className={classNames(styles.input)}
            fullWidth={true}
            id="pointsNum"
            name="pointsNum"
            type="number"
            step={1}
            min={0}
            placeholder="Number of points"
            onChange={formik.handleChange}
            value={formik.values.pointsNum}
          />
        </div>

        <div className={classNames(styles.inputField, styles.richInputField)}>
          <label className={styles.fieldLabel} htmlFor="description">
            Description
          </label>
          <AscRichTextEditor
            className={classNames(styles.richInput)}
            ref={descriptionRichTextRef}
            name="description"
            placeholder="Number of points"
            onJsonChange={(value) => {
              formik.setFieldTouched(
                "description",
                value !== formik.values.description
              );
              formik.setFieldValue("description", value);
            }}
            initialInferValue={formik.values.description}
            inputWrapperClassname={styles.richInputArea}
          />
        </div>
      </div>
      <AscAddModuleFooter
        className={styles.footer}
        currentValue={value}
        touched={touched}
        disabled={!formik.isValid}
        goNext={() => submitAndNext()}
      />
    </div>
  );
}
